import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography, useTheme } from "@mui/material";

/** ===================== Empresas (como no original) ===================== **/
const COMPANY_MAP = {
  2496: "SÃO PEDRO",
  2497: "INTEGRAÇÃO",
  2498: "VIA VERDE",
  2499: "COROADO",
  2500: "GLOBAL",
  2501: "LÍDER",
  2502: "VEGA",
};
const COMPANY_IDS = Object.keys(COMPANY_MAP).map(Number);

// Mapa inverso (nome -> id) em forma normalizada
const normalize = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .trim();

const COMPANY_ID_BY_NAME_NORM = Object.fromEntries(
  Object.entries(COMPANY_MAP).map(([id, name]) => [normalize(name), String(id)])
);

/** ===================== Utils & infra ===================== **/
// Configuração versátil de API
const API_BASE = process.env.REACT_APP_API_BASE || "https://control-api-connectbus.viacaosaopedro.com";

const CANDIDATE_URLS = [
  `${API_BASE}/api/penalizacoes/ranking`,
].filter(Boolean);

console.log('🔍 RankingPenalizacoes - API_BASE:', API_BASE);
console.log('🔍 RankingPenalizacoes - CANDIDATE_URLS:', CANDIDATE_URLS);

async function fetchWithFallback(qs, signal) {
  let lastErr;
  for (const base of CANDIDATE_URLS) {
    try {
      const res = await fetch(`${base}${qs}`, { signal });
      if (res.ok) return res.json();
      lastErr = new Error(`HTTP ${res.status} em ${base}`);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Falha ao buscar ranking");
}

const pad = (n) => String(n).padStart(2, "0");
const labelBR = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
const ymdKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toApiStr = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}:${pad(d.getSeconds())}`;

function parseDateLoose(x) {
  if (x instanceof Date) return new Date(x);
  const s = String(x || "").trim();
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
  m = s.replace(/\+/g, " ").match(/^(\d{4})-((\d{2}))-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +(m[5] || 0), +(m[6] || 0), +(m[7] || 0));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Normaliza chave de data para 'YYYY-MM-DD'
function canonYMD(key) {
  const s = String(key || "").trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = parseDateLoose(s);
  return d ? ymdKey(d) : s;
}

/** Período do mês (se for o mês corrente, vai até hoje 23:59:59) */
function buildMonthRangeForRanking(inicio, fim) {
  const di = parseDateLoose(inicio);
  const df = parseDateLoose(fim);
  const ref = df || di || new Date();

  const first = new Date(ref.getFullYear(), ref.getMonth(), 1, 0, 0, 0, 0);
  const endOfMonth = new Date(ref.getFullYear(), ref.getMonth() + 1, 0, 23, 59, 59, 999);

  const today = new Date();
  const isCurrentMonth =
    ref.getFullYear() === today.getFullYear() && ref.getMonth() === today.getMonth();

  return {
    d0: first,
    d1: isCurrentMonth
      ? new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      : endOfMonth,
  };
}

const fmt2 = (n) =>
  (Number.isFinite(n) ? n : Number(n) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// armazena valores em map sob múltiplas chaves equivalentes (id, nome, nome normalizado)
function put(map, key, ymd, rawVal) {
  const v = Number(rawVal) || 0;
  const kRaw = String(key).trim();
  const kNorm = normalize(kRaw);

  const set = (k) => {
    map[k] = map[k] || {};
    map[k][ymd] = (map[k][ymd] || 0) + v;
  };

  set(kRaw);
  if (kNorm && kNorm !== kRaw) set(kNorm);
  const maybeId = COMPANY_ID_BY_NAME_NORM[kNorm];
  if (maybeId && maybeId !== kRaw && maybeId !== kNorm) set(maybeId);
}

function get(map, idStr, nome, ymd) {
  const candidates = [
    idStr,
    String(+idStr),
    nome,
    normalize(nome),
    COMPANY_ID_BY_NAME_NORM[normalize(nome)],
  ].filter(Boolean);
  for (const k of candidates) {
    const v = map?.[k]?.[ymd];
    if (v != null) return Number(v) || 0;
  }
  return 0;
}

/** ===================== Componente ===================== **/
export default function RankingPenalizacoesPorEmpresa({
  titulo = "Ranking de Penalizações por Empresa",
  inicio,
  fim,
  empresas = COMPANY_IDS,
}) {

  const theme = useTheme();
  const rootRef = React.useRef(null);

  const [state, setState] = React.useState({
    loading: false,
    error: null,
    cols: [],
    rows: [],
    bottomRow: null,
  });

  // tick de atualização a cada 1 min
  const [refreshTick, setRefreshTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setRefreshTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // >>>>>> sincroniza a coluna fixa com o deslocamento real (scroll + virtualização)
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    root.style.setProperty("--sticky-left", "0px");

    const scroller = root.querySelector(".MuiDataGrid-virtualScroller");
    const headersInner = root.querySelector(".MuiDataGrid-columnHeadersInner");
    const renderZone = root.querySelector(".MuiDataGrid-virtualScrollerRenderZone");

    const getTranslateX = (el) => {
      if (!el) return 0;
      const tr = getComputedStyle(el).transform;
      if (!tr || tr === "none") return 0;

      // matrix(a,b,c,d, tx, ty)
      const m2d = tr.match(/^matrix\(([^)]+)\)$/);
      if (m2d) {
        const parts = m2d[1].split(",").map((s) => parseFloat(s));
        return Number.isFinite(parts[4]) ? parts[4] : 0;
      }

      // matrix3d(..., tx, ty, tz)
      const m3d = tr.match(/^matrix3d\(([^)]+)\)$/);
      if (m3d) {
        const parts = m3d[1].split(",").map((s) => parseFloat(s));
        return Number.isFinite(parts[12]) ? parts[12] : 0;
      }

      // translate3d(x, y, z) / translate(x, y)
      const t = tr.match(/translate(?:3d)?\(\s*([-.\d]+)px/i);
      return t ? parseFloat(t[1]) : 0;
    };

    const updateSticky = () => {
      // o header e a zona de linhas costumam ter o mesmo translateX (negativo)
      const tx =
        getTranslateX(headersInner) ||
        getTranslateX(renderZone) ||
        0;

      // se houver transform, compensa com -tx; senão, cai no scrollLeft
      const left = tx ? -tx : (scroller?.scrollLeft || 0);
      root.style.setProperty("--sticky-left", `${left}px`);
    };

    updateSticky();

    scroller?.addEventListener("scroll", updateSticky, { passive: true });

    // Observa mudanças de estilo nos wrappers (o MUI pode mexer no transform)
    const mo = new MutationObserver(updateSticky);
    if (headersInner)
      mo.observe(headersInner, { attributes: true, attributeFilter: ["style"] });
    if (renderZone)
      mo.observe(renderZone, { attributes: true, attributeFilter: ["style"] });

    return () => {
      scroller?.removeEventListener("scroll", updateSticky);
      mo.disconnect();
    };
  }, []);
  // <<<<<< fim da sincronização


  React.useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setState((s) => ({ ...s, loading: true, error: null }));

        const { d0, d1 } = buildMonthRangeForRanking(inicio, fim);

        // Gera dias/labels/fields
        const dayYMD = [];
        const dayField = [];
        const dayLabel = [];
        for (const d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
          const ymd = ymdKey(d);
          dayYMD.push(ymd);
          dayField.push(`d_${ymd}`);
          dayLabel.push(labelBR(d));
        }

        // Backend
        const params = new URLSearchParams({
          inicio: toApiStr(d0),
          fim: toApiStr(d1),
        });
        const qs = `?${params.toString()}`;

        const payload = await fetchWithFallback(qs, ac.signal);

        // Aceita payload em dois formatos:
        // A) { empresaId/nome -> { 'YYYY-MM-DD' -> valor } }
        // B) { 'YYYY-MM-DD' -> { empresaId/nome -> valor } }
        const raw = payload?.data ?? payload ?? {};
        const topKeys = Object.keys(raw || {});
        const looksLikeDate = (k) => /^\d{4}-\d{2}-\d{2}/.test(k) || /^\d{2}\/\d{2}\/\d{4}/.test(k);
        const datesAsTop =
          topKeys.length > 0 && topKeys.filter(looksLikeDate).length >= Math.ceil(topKeys.length * 0.6);

        // Normaliza para { key -> { ymd: number } }
        const mapByEmpresa = {};
        if (datesAsTop) {
          for (const [dateKey, empMap] of Object.entries(raw)) {
            const ymd = canonYMD(dateKey);
            for (const [empresaKey, val] of Object.entries(empMap || {})) {
              put(mapByEmpresa, empresaKey, ymd, Number(val) || 0);
            }
          }
        } else {
          for (const [empresaKey, daysObj] of Object.entries(raw)) {
            for (const [k, v] of Object.entries(daysObj || {})) {
              const ymd = canonYMD(k);
              put(mapByEmpresa, empresaKey, ymd, Number(v) || 0);
            }
          }
        }

        // Lista de empresas (id) em tela
        const empresasList = (empresas && empresas.length ? empresas : Object.keys(COMPANY_MAP))
          .map((id) => (Number.isFinite(+id) ? +id : id))
          .sort((a, b) => String(a).localeCompare(String(b), "pt-BR"));

        // Monta rows por empresa
        const rows = empresasList
          .map((empId) => {
            const idStr = String(empId);
            const nome = COMPANY_MAP[empId] || COMPANY_MAP[idStr] || String(empId);
            const row = { id: idStr, empresa: nome };
            let tot = 0;
            for (let i = 0; i < dayYMD.length; i++) {
              const ymd = dayYMD[i];
              const f = dayField[i];
              const v = get(mapByEmpresa, idStr, nome, ymd);
              row[f] = v;
              tot += v;
            }
            row["__totalEmpresa"] = tot;
            return row;
          })
          .sort((a, b) => (b["__totalEmpresa"] || 0) - (a["__totalEmpresa"] || 0));

        // Totais por dia
        const totals = {};
        for (let i = 0; i < dayField.length; i++) {
          const f = dayField[i];
          totals[f] = rows.reduce((acc, r) => acc + (Number(r[f]) || 0), 0);
        }

        // Linha "Total Por Dia"
        const bottomRow = {
          id: "__total_por_dia__",
          empresa: "Total Por Dia",
          ...Object.fromEntries(dayField.map((f) => [f, totals[f] || 0])),
          "__totalEmpresa": rows.reduce((a, r) => a + (Number(r["__totalEmpresa"]) || 0), 0),
        };

        // Colunas (EMPRESA fica “congelada” via sticky + var de scroll)
        const columns = [
          {
            field: "empresa",
            headerName: "EMPRESA",
            minWidth: 180,
            headerAlign: "left",
            align: "left",
            sortable: false,
            headerClassName: "sticky-empresa",
            cellClassName: "sticky-empresa",
          },
          ...dayField.map((f, i) => ({
            field: f,
            headerName: dayLabel[i],
            width: 110,
            type: "number",
            headerAlign: "center",
            align: "center",
            sortable: false,
            valueGetter: (p) => Number(p.row?.[f] ?? 0),
            renderCell: (p) => fmt2(Number(p.row?.[f] ?? 0)),
          })),
          {
            field: "__totalEmpresa",
            headerName: "Total Por Empresa",
            width: 160,
            type: "number",
            headerAlign: "center",
            align: "center",
            sortable: false,
            valueGetter: (p) =>
              typeof p.row?.["__totalEmpresa"] === "number"
                ? p.row["__totalEmpresa"]
                : Number(p.row?.["__totalEmpresa"] ?? 0),
            renderCell: (p) =>
              fmt2(
                typeof p.row?.["__totalEmpresa"] === "number"
                  ? p.row["__totalEmpresa"]
                  : Number(p.row?.["__totalEmpresa"] ?? 0)
              ),
          },
        ];

        setState({ loading: false, error: null, cols: columns, rows, bottomRow });
      } catch (error) {
        if (error?.name === "AbortError") return;
        setState({
          loading: false,
          error: error?.message || String(error),
          cols: [],
          rows: [],
          bottomRow: null,
        });
      }
    })();


    return () => {
      ac.abort();
    };
  }, [inicio, fim, refreshTick, empresas]);

  const { loading, error, cols, rows, bottomRow } = state;
  const rowsWithTotal = React.useMemo(
    () => (bottomRow ? [...rows, bottomRow] : rows),
    [rows, bottomRow]
  );

  return (
    <Box ref={rootRef} sx={{ width: "100%" }}>
      <Typography
        variant="h6"
        align="left" // título alinhado à esquerda
        sx={{
          mb: 1,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {titulo}
      </Typography>

      <DataGrid
        autoHeight
        density="compact"
        loading={loading}
        rows={rowsWithTotal}
        columns={cols}
        disableColumnMenu
        disableColumnReorder        // evita arrastar a coluna fixa
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 25 } },
        }}
        getRowClassName={(p) => (p.id === "__total_por_dia__" ? "row-total" : "")}
        sx={{
          // Coluna fixa: deslocamento atualizado pelo efeito de scroll
          "--sticky-left": "0px",

          // Header & células da coluna fixa
          "& .sticky-empresa": { position: "sticky", left: 0,
            zIndex: 4, // ligeiramente acima do default do header
            backgroundColor: theme.palette.background.paper,
          },

          // MU DataGrid desloca as células com transform; aqui anulamos
          "& .MuiDataGrid-cell.sticky-empresa, & .MuiDataGrid-columnHeader.sticky-empresa": {
            transform: "translate3d(0,0,0) !important",
          },

          // Linha divisória do lado direito da coluna fixa
          "& .MuiDataGrid-cell.sticky-empresa": {
            boxShadow: `2px 0 0 0 ${theme.palette.divider}`,
          },
        }}
        disableVirtualization
      />

      {error && (
        <Typography color="error" sx={{ mt: 1 }}>
          Erro ao carregar ranking: {error}
        </Typography>
      )}

      <style>{`.row-total .MuiDataGrid-cell{font-weight:700}`}</style>
    </Box>
  );
}
